import type { EntityProfileCalendarVisit } from "../entityProfileModal/sections/calendar/entityProfileCalendar.utils";
import type { PatientCalendarSelection } from "./sections/calendar/visits/patientCalendarSelection";
import { getFullName } from "../../shared/utils/get-full-name";
import type {
  CaregiverId,
  FaxDetailsId,
  PatientId,
  VisitBroadcastId,
  VisitInstanceId,
} from "../../shared/schema/schema";
import { reactBridge } from "../../core";
import { PatientProfilePatient } from "./patientProfileModal.types";

export type PatientCalendarSelectionRequest = {
  selection: PatientCalendarSelection;
  onSelected: (visits: EntityProfileCalendarVisit[]) => void;
  onError: (error: unknown) => void;
};

export type PatientProfileAction =
  | "openChat"
  | "openNotes"
  | "exportProfile"
  | "openExportProfileSettings"
  | "openCertificationPeriod"
  | "openCertificationPeriods"
  | "openPatientContract"
  | "uploadPatientContractFile"
  | "openAuthorization"
  | "openAuthorizationFile"
  | "uploadAuthorizationFile"
  | "openAuthorizationUtilization"
  | "deleteAuthorization"
  | "autoAllocateAuthorization"
  | "openPatientAdministrativeDiagnosisCode"
  | "openPatientAdministrativeDiagnosisCodes"
  | "openPatientBillingInvoiceDetails"
  | "openPatientBillingInvoiceExportHistory"
  | "openPatientBillingInvoices"
  | "openPatientPayRate"
  | "openPatientPayRates"
  | "openPatientPayRateReset"
  | "openPatientSurpluses"
  | "openPatientPhysician"
  | "deletePatientPhysician"
  | "updatePatientPhysicianPrimary"
  | "openPatientMedicationProfileLegacy"
  | "openPatientDocument"
  | "openPatientPlanOfCare"
  | "openPatientDutySheetExport"
  | "openPatientPlanOfCareHistory"
  | "deletePatientMedicationProfile"
  | "openPatientMeasurement"
  | "openPatientVbpReport"
  | "openLegacyPatientMedicalSection"
  | "openLinkPatient"
  | "removeLinkedPatient"
  | "openNewVisit"
  | "openNewTask"
  | "openPatientTask"
  | "cancelPatientTask"
  | "schedulePatientTask"
  | "selectPatientCalendarVisits"
  | "editPatientCalendarItems"
  | "deletePatientCalendarItems"
  | "missPatientCalendarItems"
  | "approvePatientCalendarClockLocation"
  | "openNewVacation"
  | "openNewCalendarNote"
  | "openChangeStatus"
  | "openAddPhoneNumber";

function dispatchPatientProfileAction(params: {
  action: PatientProfileAction;
  calendarDate?: string;
  patient: PatientProfilePatient;
  selectedVisits?: unknown[];
  calendarSelection?: PatientCalendarSelection;
  task?: unknown;
  data?: unknown;
  targetElementId?: string;
  onDone?: () => void;
}) {
  window.dispatchEvent(
    new CustomEvent("from-webapp-react", {
      detail: {
        type: "patientProfileAction",
        payload: {
          action: params.action,
          calendarDate: params.calendarDate,
          patientId: params.patient.id,
          patientName: getFullName(params.patient),
          patient: params.patient,
          selectedVisits: params.selectedVisits,
          calendarSelection: params.calendarSelection,
          task: params.task,
          data: params.data,
          targetElementId: params.targetElementId,
          onDone: params.onDone,
        },
      },
    })
  );
}

type PatientActionParams = {
  calendarDate?: string;
  data?: unknown;
  onDone?: () => void;
  patient: PatientProfilePatient;
  selectedVisits?: unknown[];
  calendarSelection?: PatientCalendarSelection;
  targetElementId?: string;
  task?: unknown;
};

function openAction(action: PatientProfileAction, params: PatientActionParams) {
  dispatchPatientProfileAction({ action, ...params });
}

export type PatientProfileBridgeActions = {
  selectCalendarVisits: (params: {
    patient: PatientProfilePatient;
    selectedVisits: EntityProfileCalendarVisit[];
    selection: PatientCalendarSelection;
  }) => Promise<EntityProfileCalendarVisit[]>;
  approveCalendarClockLocation: (params: PatientActionParams) => void;
  cancelTask: (params: PatientActionParams) => void;
  deleteCalendarItems: (params: PatientActionParams) => void;
  editCalendarItems: (params: PatientActionParams) => void;
  exportProfile: (params: PatientActionParams) => void;
  linkPatient: (params: PatientActionParams) => void;
  missCalendarItems: (params: PatientActionParams) => void;
  navigateToCaregiver: (caregiverId: CaregiverId) => void;
  navigateToDocument: (documentId: FaxDetailsId) => void;
  navigateToPatient: (patientId: PatientId) => void;
  navigateToVisitBroadcast: (visitBroadcastId: VisitBroadcastId) => void;
  navigateToVisitInstance: (visitInstanceId: VisitInstanceId) => void;
  openAddPhoneNumber: (params: PatientActionParams) => void;
  openAuthorization: (params: PatientActionParams) => void;
  openPatientAdministrativeDiagnosisCode: (params: PatientActionParams) => void;
  openPatientAdministrativeDiagnosisCodes: (params: PatientActionParams) => void;
  openBillingInvoiceDetails: (params: PatientActionParams) => void;
  openBillingInvoiceExportHistory: (params: PatientActionParams) => void;
  openBillingInvoices: (params: PatientActionParams) => void;
  openCertificationPeriod: (params: PatientActionParams) => void;
  openCertificationPeriods: (params: PatientActionParams) => void;
  openChangeStatus: (params: PatientActionParams) => void;
  openChat: (params: PatientActionParams) => void;
  openCustomAddress: (params: { patient: PatientProfilePatient }) => void;
  openDialer: (params: { predefinedPhoneNumber: string }) => void;
  openExportProfileSettings: (params: PatientActionParams) => void;
  openIntakeModal: (params: { patient: PatientProfilePatient }) => void;
  openLegacyPatientMedicalSection: (params: PatientActionParams) => void;
  openNewCalendarNote: (params: PatientActionParams) => void;
  openNewTask: (params: PatientActionParams) => void;
  openNewVacation: (params: PatientActionParams) => void;
  openNewVisit: (params: PatientActionParams) => void;
  openNotes: (params: PatientActionParams) => void;
  openPatientContract: (params: PatientActionParams) => void;
  uploadPatientContractFile: (params: PatientActionParams) => void;
  openPatientMeasurement: (params: PatientActionParams) => void;
  openPatientVbpReport: (params: PatientActionParams) => void;
  openPatientPayRate: (params: PatientActionParams) => void;
  openPatientPayRates: (params: PatientActionParams) => void;
  openPatientPayRateReset: (params: PatientActionParams) => void;
  openPatientPhysician: (params: PatientActionParams) => void;
  deletePatientPhysician: (params: PatientActionParams) => void;
  updatePatientPhysicianPrimary: (params: PatientActionParams) => void;
  openPatientMedicationProfileLegacy: (params: PatientActionParams) => void;
  openPatientDocument: (params: PatientActionParams) => void;
  openPatientDutySheetExport: (params: PatientActionParams) => void;
  openPatientPlanOfCare: (params: PatientActionParams) => void;
  openPatientPlanOfCareHistory: (params: PatientActionParams) => void;
  deletePatientMedicationProfile: (params: PatientActionParams) => void;
  openPatientSurpluses: (params: PatientActionParams) => void;
  openAuthorizationFile: (params: PatientActionParams) => void;
  uploadAuthorizationFile: (params: PatientActionParams) => void;
  openAuthorizationUtilization: (params: PatientActionParams) => void;
  deleteAuthorization: (params: PatientActionParams) => void;
  autoAllocateAuthorization: (params: PatientActionParams) => void;
  openTaskActivity: (params: { patientId: PatientId }) => void;
  openTask: (params: PatientActionParams) => void;
  removeLinkedPatient: (params: PatientActionParams) => void;
  scheduleTask: (params: PatientActionParams) => void;
};

export const patientProfileBridgeActions: PatientProfileBridgeActions = {
  selectCalendarVisits: (params) =>
    new Promise((onSelected, onError) => {
      openAction("selectPatientCalendarVisits", {
        patient: params.patient,
        selectedVisits: params.selectedVisits,
        data: {
          selection: params.selection,
          onSelected,
          onError,
        } satisfies PatientCalendarSelectionRequest,
      });
    }),
  approveCalendarClockLocation: (params) =>
    openAction("approvePatientCalendarClockLocation", params),
  cancelTask: (params) => openAction("cancelPatientTask", params),
  deleteCalendarItems: (params) => openAction("deletePatientCalendarItems", params),
  editCalendarItems: (params) => openAction("editPatientCalendarItems", params),
  exportProfile: (params) => openAction("exportProfile", params),
  linkPatient: (params) => openAction("openLinkPatient", params),
  missCalendarItems: (params) => openAction("missPatientCalendarItems", params),
  navigateToCaregiver: (caregiverId) => {
    window.dispatchEvent(
      new CustomEvent("from-webapp-react", {
        detail: {
          type: "navigate",
          payload: { id: caregiverId, entity: "Caregiver" },
          redirect: true,
        },
      })
    );
  },
  navigateToDocument: (documentId) => {
    window.dispatchEvent(
      new CustomEvent("from-webapp-react", {
        detail: { type: "navigate", payload: { id: documentId, entity: "Document" } },
      })
    );
  },
  navigateToPatient: (patientId) => {
    window.dispatchEvent(
      new CustomEvent("from-webapp-react", {
        detail: { type: "navigate", payload: { id: patientId, entity: "Patient" } },
      })
    );
  },
  navigateToVisitBroadcast: (visitBroadcastId) => {
    window.dispatchEvent(
      new CustomEvent("from-webapp-react", {
        detail: { type: "navigate", payload: { id: visitBroadcastId, entity: "VisitBroadcast" } },
      })
    );
  },
  navigateToVisitInstance: (visitInstanceId) => {
    window.dispatchEvent(
      new CustomEvent("from-webapp-react", {
        detail: { type: "navigate", payload: { id: visitInstanceId, entity: "VisitInstance" } },
      })
    );
  },
  openAddPhoneNumber: (params) => openAction("openAddPhoneNumber", params),
  openAuthorization: (params) => openAction("openAuthorization", params),
  openPatientAdministrativeDiagnosisCode: (params) =>
    openAction("openPatientAdministrativeDiagnosisCode", params),
  openPatientAdministrativeDiagnosisCodes: (params) =>
    openAction("openPatientAdministrativeDiagnosisCodes", params),
  openBillingInvoiceDetails: (params) => openAction("openPatientBillingInvoiceDetails", params),
  openBillingInvoiceExportHistory: (params) =>
    openAction("openPatientBillingInvoiceExportHistory", params),
  openBillingInvoices: (params) => openAction("openPatientBillingInvoices", params),
  openCertificationPeriod: (params) => openAction("openCertificationPeriod", params),
  openCertificationPeriods: (params) => openAction("openCertificationPeriods", params),
  openChangeStatus: (params) => openAction("openChangeStatus", params),
  openChat: (params) => openAction("openChat", params),
  openCustomAddress: (params) => {
    reactBridge.dispatch("CUSTOM_ADDRESS:OPEN_MODAL", {
      entity: "Patient",
      id: params.patient.id,
    });
  },
  openDialer: (params) => {
    reactBridge.dispatch("CALL_CENTER:OPEN_DIALER_POPUP", params);
  },
  openExportProfileSettings: (params) => openAction("openExportProfileSettings", params),
  openIntakeModal: (params) => {
    window.dispatchEvent(
      new CustomEvent("from-webapp-react", {
        detail: {
          type: "patientProfileAction",
          payload: {
            action: "openIntakeModal",
            patientId: params.patient.id,
            patient: params.patient,
          },
        },
      })
    );
  },
  openLegacyPatientMedicalSection: (params) =>
    openAction("openLegacyPatientMedicalSection", params),
  openNewCalendarNote: (params) => openAction("openNewCalendarNote", params),
  openNewTask: (params) => openAction("openNewTask", params),
  openNewVacation: (params) => openAction("openNewVacation", params),
  openNewVisit: (params) => openAction("openNewVisit", params),
  openNotes: (params) => openAction("openNotes", params),
  openPatientContract: (params) => openAction("openPatientContract", params),
  uploadPatientContractFile: (params) => openAction("uploadPatientContractFile", params),
  openPatientMeasurement: (params) => openAction("openPatientMeasurement", params),
  openPatientVbpReport: (params) => openAction("openPatientVbpReport", params),
  openPatientPayRate: (params) => openAction("openPatientPayRate", params),
  openPatientPayRates: (params) => openAction("openPatientPayRates", params),
  openPatientPayRateReset: (params) => openAction("openPatientPayRateReset", params),
  openPatientPhysician: (params) => openAction("openPatientPhysician", params),
  deletePatientPhysician: (params) => openAction("deletePatientPhysician", params),
  updatePatientPhysicianPrimary: (params) => openAction("updatePatientPhysicianPrimary", params),
  openPatientMedicationProfileLegacy: (params) =>
    openAction("openPatientMedicationProfileLegacy", params),
  openPatientDocument: (params) => openAction("openPatientDocument", params),
  openPatientDutySheetExport: (params) => openAction("openPatientDutySheetExport", params),
  openPatientPlanOfCare: (params) => openAction("openPatientPlanOfCare", params),
  openPatientPlanOfCareHistory: (params) => openAction("openPatientPlanOfCareHistory", params),
  deletePatientMedicationProfile: (params) => openAction("deletePatientMedicationProfile", params),
  openPatientSurpluses: (params) => openAction("openPatientSurpluses", params),
  openAuthorizationFile: (params) => openAction("openAuthorizationFile", params),
  uploadAuthorizationFile: (params) => openAction("uploadAuthorizationFile", params),
  openAuthorizationUtilization: (params) => openAction("openAuthorizationUtilization", params),
  deleteAuthorization: (params) => openAction("deleteAuthorization", params),
  autoAllocateAuthorization: (params) => openAction("autoAllocateAuthorization", params),
  openTaskActivity: (params) => {
    reactBridge.dispatch("PATIENT:OPEN_TASK_ACTIVITY", params);
  },
  openTask: (params) => openAction("openPatientTask", params),
  removeLinkedPatient: (params) => openAction("removeLinkedPatient", params),
  scheduleTask: (params) => openAction("schedulePatientTask", params),
};
